import React, {useRef, useState} from 'react';
import {SpinLoading, Toast} from 'antd-mobile';
import {ScanComponent} from '../../../components/ScanComponent';
import {getNavBarContainerStyle, getBottomNavBarContainerStyle} from "../../../utils/deviceUtils";
import {t} from "../../../i18n";
import {client} from "../../../graphql/client";
import NetworkService from "../../../services/NetworkService";
import {normalizeParcelScan} from "../../../utils/thirdPartyLabelScanUtils";
import {
    CHECK_PICKUP_TRACKING_NUMBER_EXISTS,
    CheckPickupTrackingNumberExistsResponse
} from "../graphql/queries/checkPickupTrackingNumberExists";

interface PickUpTrackingNumberScannerProps {
    visible: boolean;
    trackingNumbers: string[];
    // 弱网/无网下跳过校验入列的运单号，列表里标红点
    unverifiedTrackingNumbers: string[];
    onChange: (next: string[]) => void;
    onMarkUnverified: (trackingNumber: string) => void;
    onClose: () => void;
}

// 校验超时：超过即当作弱网/无网放行入列。5s 偏向校验有效性——仓库现场弱网首字节常要 2~5s，
// 压得太短会让校验恰好在最需要它的场景失效；代价是弱网下行最长 5 秒才出现，
// 由「校验中 N 件」计数兜住反馈。调它只影响行多久出现，不会造成已入列的条目被事后移除
const CHECK_TIMEOUT_MS = 5000;
const VIBRATE_MS = 200;

// 运单号连续扫描页：一次进入可连扫多单，扫到即入列表，右下角铅笔可手输。
// 只管扫码，不管取位——司机位置由外层卡片在打开本页时统一取
const PickUpTrackingNumberScanner: React.FC<PickUpTrackingNumberScannerProps> = ({
    visible,
    trackingNumbers,
    unverifiedTrackingNumbers,
    onChange,
    onMarkUnverified,
    onClose,
}) => {
    // 正在校验中的单号，仅用于本页顶部计数展示，不影响提交数据
    const [checkingTrackingNumbers, setCheckingTrackingNumbers] = useState<string[]>([]);
    // 校验请求在飞行中的单号：行还没出现时连扫会反复识别到同一张面单，靠它挡住重复请求
    const inFlightRef = useRef<Set<string>>(new Set());
    // 校验结果回来时列表可能已经变了（司机在这期间又扫进来几件、或手动删了几条），
    // 入列必须以最新值为基准，不能用闭包里捕获的旧数组
    const trackingNumbersRef = useRef<string[]>(trackingNumbers);
    trackingNumbersRef.current = trackingNumbers;

    // 每跳过一件提示一次（用户明确要求）。antd-mobile 的 Toast 是单例，连扫时后一条顶掉前一条，
    // 弱网持续期间等效于一直挂着这条提示——正是想要的效果：司机随时知道这批单号没校验过
    const notifyCheckSkipped = () => {
        Toast.show({content: t('pages.pickUp.pickedUp.trackingNumberCheckSkipped'), position: 'top'});
    };

    // 新扫的插到最前面：连扫上百件时列表会滚动，最后扫的那件必须在视野里，
    // 否则司机看不到刚才那一下到底进没进。
    // verified=false（弱网跳过校验）的单号额外上报给页面，列表里标红点
    const prependTrackingNumber = (trackingNumber: string, verified: boolean) => {
        const normalized = normalizeParcelScan(trackingNumber);
        if (trackingNumbersRef.current.some((item) => normalizeParcelScan(item) === normalized)) {
            return;
        }
        onChange([trackingNumber, ...trackingNumbersRef.current]);
        if (!verified) {
            onMarkUnverified(trackingNumber);
        }
    };

    // 校验通过才入列。不采用"先入列、判定不存在再移除"：那样会出现司机没盯着扫描页时
    // 列表条目突然消失（关页面后在途结果、网络恢复补校验等），现场无法接受。
    // 这里不阻塞扫描——相机一直在跑，慢的只是行出现的时间，顶部「校验中 N 件」兜住即时反馈
    const checkTrackingNumber = async (trackingNumber: string) => {
        // 在飞集合按归一化值记，同一张面单两种格式在飞时也只发一次请求
        const normalized = normalizeParcelScan(trackingNumber);
        inFlightRef.current.add(normalized);
        setCheckingTrackingNumbers((prev) => [...prev, normalized]);
        try {
            // navigator.onLine 只判得出系统层面断网（飞行模式/关 WiFi），判不出弱网，
            // 这里只作为"省掉一次注定失败的请求"的短路；真正兜住弱网的是下面的超时
            if (!NetworkService.isNetworkAvailable()) {
                notifyCheckSkipped();
                prependTrackingNumber(trackingNumber, false);
                return;
            }
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), CHECK_TIMEOUT_MS);
            try {
                const {data} = await client.query<CheckPickupTrackingNumberExistsResponse>({
                    query: CHECK_PICKUP_TRACKING_NUMBER_EXISTS,
                    variables: {trackingNumber},
                    fetchPolicy: 'network-only',
                    errorPolicy: 'all',
                    context: {fetchOptions: {signal: controller.signal}},
                });
                if (data?.checkPickupTrackingNumberExists === true) {
                    prependTrackingNumber(trackingNumber, true);
                } else if (data?.checkPickupTrackingNumberExists === false) {
                    // 明确不存在：不入列，带上单号提示——弱网下司机可能已经扫了后面几件，
                    // 不说是哪一件他无从判断
                    try {
                        navigator.vibrate?.(VIBRATE_MS);
                    } catch {
                        // iOS WKWebView 不支持震动，静默
                    }
                    Toast.show({
                        content: t('pages.pickUp.pickedUp.trackingNumberNotFound', {trackingNumber: normalized}),
                        position: 'top',
                        duration: 3000,
                    });
                } else {
                    // 请求通了但没拿到有效结果（errorPolicy: 'all' 下 GraphQL 业务异常不抛错）。
                    // 这不是弱网，是服务端问题，但同样放行——服务端故障不该让司机卡在站点门口
                    notifyCheckSkipped();
                    prependTrackingNumber(trackingNumber, false);
                }
            } finally {
                clearTimeout(timeoutId);
            }
        } catch (error) {
            // 超时(abort) / 网络错误：按弱网处理，直接放行入列
            console.warn('Tracking number check skipped:', trackingNumber, error);
            notifyCheckSkipped();
            prependTrackingNumber(trackingNumber, false);
        } finally {
            inFlightRef.current.delete(normalized);
            setCheckingTrackingNumbers((prev) => prev.filter((item) => item !== normalized));
        }
    };

    const handleScanSuccess = (text: string) => {
        // 列表与提交都保留扫码原文（含 GS 分隔符等），后端落库日志里才能看到司机到底扫出了什么；
        // 归一化（USPS 取末 22 位、FedEx 取末 14 位）只用于本地去重与展示，规则与后端 ThirdPartyLabelScanParser 一致。
        // JS 的 trim 不去 0x1D，原文里的 GS 会原样保留
        const trackingNumber = text.trim();
        if (!trackingNumber) {
            return;
        }
        const normalized = normalizeParcelScan(trackingNumber);
        // 连扫时同一张面单容易被识别多次；PDA（0x1D）和手机（字面 {GS}）扫同一张原文不同，按归一化值判重
        if (trackingNumbers.some((item) => normalizeParcelScan(item) === normalized)) {
            Toast.show({content: t('pages.pickUp.pickedUp.trackingNumberDuplicated'), position: 'center'});
            return;
        }
        if (inFlightRef.current.has(normalized)) {
            return;
        }
        void checkTrackingNumber(trackingNumber);
    };

    if (!visible) {
        return null;
    }

    return (
        <div className="fixed inset-0 flex flex-col" style={{background: '#14141C', zIndex: 200}}>
            <div {...getNavBarContainerStyle()}>
                <div className="flex items-center gap-2.5 px-4 py-3">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="shrink-0" onClick={onClose}>
                        <path d="M13 2L4 10l9 8" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <div className="flex-1 text-center text-base font-bold text-white mr-5">
                        {t('pages.pickUp.pickedUp.scanTrackingNumber')}
                    </div>
                </div>
            </div>

            {/* 相机区：扫码组件自带手动输入入口，扫不出来时可手打单号 */}
            <div className="relative flex-1">
                <ScanComponent onScanSuccess={handleScanSuccess}/>
            </div>

            <div className="px-6 pt-3.5 text-center text-xs" style={{color: '#C9C9D6'}}>
                {t('pages.pickUp.pickedUp.trackingNumberScanHint')}
            </div>

            {/* 校验中计数：校验通过才入列，弱网下行要等几秒才出现，
                没有这个反馈司机会以为没扫上、反复扫同一张 */}
            {checkingTrackingNumbers.length > 0 && (
                <div className="flex items-center justify-center gap-1.5 px-6 pt-2.5">
                    <SpinLoading style={{'--size': '12px', '--color': '#C9C9D6'} as React.CSSProperties}/>
                    <span className="text-xs" style={{color: '#C9C9D6'}}>
                        {t('pages.pickUp.pickedUp.trackingNumberChecking', {count: checkingTrackingNumbers.length})}
                    </span>
                </div>
            )}

            {/* 已扫列表：就地可删，避免扫错还要退出去改 */}
            <div className="overflow-y-auto px-6 pt-3.5" style={{maxHeight: '30vh'}}>
                {trackingNumbers.map((trackingNumber, index) => (
                    <div
                        key={trackingNumber}
                        className="mb-1.5 flex items-center gap-2.5 rounded-lg px-2.5 py-2"
                        style={{background: 'rgba(255, 255, 255, 0.08)'}}
                    >
                        {/* 紫点=已校验，红点=弱网下跳过校验 */}
                        <span
                            className="h-1.5 w-1.5 shrink-0 rounded-full"
                            style={{background: unverifiedTrackingNumbers.includes(trackingNumber) ? '#FF4D4F' : '#5D3FEF'}}
                        />
                        {/* 展示归一化后的运单号，state 里仍是扫码原文 */}
                        <span className="flex-1 break-all text-xs text-white">{normalizeParcelScan(trackingNumber)}</span>
                        <svg
                            width="13"
                            height="13"
                            viewBox="0 0 14 14"
                            fill="none"
                            className="shrink-0"
                            onClick={() => onChange(trackingNumbers.filter((_, i) => i !== index))}
                        >
                            <path d="M2 2l10 10M12 2L2 12" stroke="#8A8A93" strokeWidth="1.6" strokeLinecap="round"/>
                        </svg>
                    </div>
                ))}
            </div>

            <div {...getBottomNavBarContainerStyle('flex-0 px-6 py-3 mt-auto')}>
                <div
                    className="rounded-full py-3.5 text-center text-base font-semibold text-white"
                    style={{background: 'var(--adm-color-primary)'}}
                    onClick={onClose}
                >
                    {t('pages.pickUp.pickedUp.trackingNumberScanDone', {count: trackingNumbers.length})}
                </div>
            </div>
        </div>
    );
};

export default PickUpTrackingNumberScanner;
